/*
 * Simple date service library, check if transition to momentjs/luxon.
 */

/**
 * Get the required next dayOfWeek.
 * @param param0.dayOfWeek desidered day of week (1 - Monday..7 - Sunday)
 * @param param0.startDate startDate, default to current date
 * @param param0.includeStart consider startdate as valid date, if it is dayOfWeek it is returned.
 * @param param0.inUTC perform logic using UTC dates. Return it is considered as UTC based date.
 * @returns Date
 */
export function getNextDayDate({
  dayOfWeek,
  startDate,
  includeStart = false,
  inUTC = false,
}: {
  dayOfWeek: number;
  startDate?: Date;
  includeStart?: boolean;
  inUTC?: boolean;
}): Date {
  const d = startDate ? new Date(startDate) : new Date();
  const pad = includeStart ? 0 : 7;
  if (inUTC) {
    d.setUTCHours(0, 0, 0, 0); // reset to midnight
    d.setUTCDate(d.getUTCDate() + ((dayOfWeek + 7 - d.getUTCDay()) % 7 || pad));
  } else {
    d.setHours(0, 0, 0, 0); // reset to midnight
    d.setDate(d.getDate() + ((dayOfWeek + 7 - d.getDay()) % 7 || pad));
  }
  return d;
}

/**
 * Compares two dates without considering time part.
 * Dates are normalized to UTC before compare them.
 * @param a date
 * @param b date
 * @returns 0 if equals, 1 if a > b, -1 if a < b
 */
export function compareDates(a: Date, b: Date) {
  const convert = (date: Date) => {
    const d = new Date(date);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  const aUTCDate = convert(a).valueOf();
  const bUTCDate = convert(b).valueOf();
  if (aUTCDate === bUTCDate) return 0;
  return aUTCDate > bUTCDate ? 1 : -1;
}

/**
 * Safe date toLocaleDateString.
 * For invalid locales the system default one will be used as fallback unless throwOnMissingLocale is set.
 * Invalid date values or null are resolved as Invalid Date output.
 * @param date date
 * @param locales The locale(s) to use
 * @param options Format options
 * @param throwOnMissingLocale boolean. If true throws if invalid provided locale. Default false.
 * @returns formatted locale date string
 */
export function toLocaleDateString(
  date: Date | string | number | null,
  locales?: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions,
  throwOnMissingLocale = false
) {
  // if date is null, null is casted to 0 -> date fallbacks to 1/1/1970
  // we want to get Invalid Date instead.
  const _date = date === null ? Number.NaN : date;
  const d = new Date(_date);
  try {
    return d.toLocaleDateString(locales, options);
  } catch (e) {
    if (throwOnMissingLocale) throw e;
    return d.toLocaleDateString(undefined, options);
  }
}
